'use client';

import { Input } from '@app/app/_components/Input/Input';
import { AddressInput } from '@app/app/_components/AddressInput/AddressInput';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Heading } from '@app/app/_components/Heading/Heading';
import { useTranslations } from 'next-intl';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { CountryCode } from '@app/domain/model/CountryCode';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/usePolicyholderFormFields';
import { CommonType } from '@app/domain/model/CommonType';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';

interface Props {
    formState: FormState<PurchaseTravelInsuranceResult>;
    isCompany: boolean;
    personalCode: string;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    disabledCheckbox?: boolean;
}

export function PolicyholderInformationStep({
    formState,
    isCompany,
    personalCode,
    policyholderFields,
    policyholderHandlers,
    disabledCheckbox,
}: Props) {
    const t = useTranslations();

    return (
        <>
            <div className="flex flex-col gap-4">
                <Heading level="h4">
                    {isCompany ? t('profile.companyInformation') : t('eshop.travel.personalInformation')}
                </Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                        name="policyholderFirstName"
                        label={isCompany ? t('profile.companyNameLabel') : t('eshop.travel.firstNameLabel')}
                        placeholder={
                            isCompany ? t('profile.companyNamePlaceholder') : t('eshop.travel.firstNamePlaceholder')
                        }
                        value={policyholderFields.firstName}
                        onChange={policyholderHandlers.handleFirstNameChange}
                        error={formState.violations?.policyholderFirstName}
                    />
                    {!isCompany && (
                        <Input
                            name="policyholderLastName"
                            label={t('eshop.travel.lastNameLabel')}
                            placeholder={t('eshop.travel.lastNamePlaceholder')}
                            value={policyholderFields.lastName}
                            onChange={policyholderHandlers.handleLastNameChange}
                            error={formState.violations?.policyholderLastName}
                        />
                    )}
                    {personalCode && (
                        <ReadOnlyField
                            label={isCompany ? t('profile.companyCodeLabel') : t('eshop.travel.personalCodeLabel')}>
                            {personalCode}
                        </ReadOnlyField>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.travel.contactInformation')}</Heading>
                <AddressInput
                    disableCountrySelect
                    defaultCountry={CountryCode.Lithuania}
                    countryOptions={[]}
                    namePrefix="policyholder"
                    forceSearchOnStart
                    violations={formState.violations}
                    defaultValue={policyholderFields.address ?? undefined}
                    onChange={policyholderHandlers.handleAddressChange}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                    name="policyholderPhoneNumber"
                    label={t('eshop.travel.phoneNumberLabel')}
                    placeholder={t('eshop.travel.phoneNumberPlaceholder')}
                    value={policyholderFields.phoneNumber}
                    onChange={policyholderHandlers.handlePhoneNumberChange}
                    prefix="+370"
                    error={formState.violations?.policyholderPhoneNumber}
                />
                <Input
                    name="policyholderEmail"
                    label={t('eshop.travel.emailLabel')}
                    placeholder={t('eshop.travel.emailPlaceholder')}
                    description={t('eshop.travel.emailDescription')}
                    value={policyholderFields.email}
                    onChange={policyholderHandlers.handleEmailChange}
                    error={formState.violations?.policyholderEmail}
                />
            </div>

            {!isCompany && !disabledCheckbox && (
                <CheckboxInput
                    name="policyholderIsTraveler"
                    label={t('eshop.travel.policyholderIsTraveler')}
                    checked={policyholderFields.isTraveler}
                    onChange={policyholderHandlers.handleIsTravelerChange}
                />
            )}

            {disabledCheckbox && (
                <input
                    type="hidden"
                    name="policyholderIsTraveler"
                    value={policyholderFields.isTraveler ? CommonType.True : CommonType.False}
                />
            )}
        </>
    );
}
